import { Form, FormInstance, Input, Spin, Button } from 'antd';
import { FormField, FormSelect } from '../form/FormBasic';
import { FormFieldTypeEnum } from '../../enums/FormFieldTypeEnum';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { referenceDataSyncService } from '../../services/referenceDataSyncService';
import {
  REGEX_ALL_ALLOWED_SAFE_FILTER,
  REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER,
} from '../../constants/regex';
import { DeleteOutlined } from '@ant-design/icons';
import { Plus } from 'react-bootstrap-icons';
import { ProductUnitEnum } from '../../enums/ProductUnitEnum';
import {
  maxLengthRule,
  minLengthRule,
  noSpacesOnlyRule,
  regexRule,
  requiredRule,
} from '../../helpers/formHelper';
import { normalize } from '../../utils/normalizeWords';

export interface IMainProduct {
  name: string;
  productCategory: string;
  unit: ProductUnitEnum;
  presentations: string[];
  brands?: string[];
}

export interface IMainProductRecord extends IMainProduct {
  id: string;
  createdAt: string;
}
interface AddMainProductFormProps {
  form: FormInstance;
  handleCancel: () => void;
  getConfirmation: (values: IMainProduct) => void;
  isEdit?: boolean;
  initialCategoryOptions?: { label: string; value: string }[];
  isEditDataLoading?: boolean;
}

const AddMainProductForm: React.FC<AddMainProductFormProps> = ({
  form,
  handleCancel,
  getConfirmation,
  isEdit,
  initialCategoryOptions,
  isEditDataLoading,
}) => {
  const { t } = useTranslation(['mainProduct', 'form']);

  const [mainCategoryOptions, setMainCategoryOptions] = useState<any>();

  const [initialPresentationsCount, setInitialPresentationsCount] = useState<number>(0);
  const [initialBrandsCount, setInitialBrandsCount] = useState<number>(0);

  useEffect(() => {
    getProductCategories();
  }, []);

  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!isEditDataLoading) {
      isInitializedRef.current = false;
    }
  }, [isEditDataLoading, isEdit]);

  useEffect(() => {
    if (!isEdit) {
      setInitialPresentationsCount(0);
      setInitialBrandsCount(0);
      return;
    }

    if (isEditDataLoading || isInitializedRef.current) {
      return;
    }

    const presentations = form.getFieldValue('presentations') || [];
    const brands = form.getFieldValue('brands') || [];

    setInitialPresentationsCount(presentations.length);
    setInitialBrandsCount(brands.length);

    isInitializedRef.current = true;
  }, [isEdit, isEditDataLoading]);

  useEffect(() => {
    if (initialCategoryOptions && initialCategoryOptions.length > 0) {
      setMainCategoryOptions(initialCategoryOptions);
    }
  }, [initialCategoryOptions]);

  const getProductCategories = async () => {
    try {
      const categoriesData = await referenceDataSyncService.getProductCategories();
      const categoryOptionArr = categoriesData.map((category: any) => ({
        label: `${category.mainCategory}${category.category ? ` > ${category.category}` : ''}`,
        value: category?.id?.toString(),
        id: category?.id,
      }));

      setMainCategoryOptions(categoryOptionArr);
    } catch (error) {
      console.error('Error loading product categories:', error);
      setMainCategoryOptions([]);
    }
  };

  const handleMainCategoryChange = async (val: any) => {
    if (val && mainCategoryOptions) {
      const selectedCategory = mainCategoryOptions.find((option: any) => option.value === val);
      if (selectedCategory) {
        form.setFieldValue('productCategoryId', selectedCategory?.id);
      }
    } else {
      form.setFieldValue('productCategoryId', undefined);
    }
  };

  return (
    <>
      {isEditDataLoading && (
        <div className="flex justify-center">
          <Spin />
        </div>
      )}

      <Form
        layout="vertical"
        form={form}
        name="addMainProductForm"
        onFinish={getConfirmation}
        className="mt-2 w-full"
      >
        {/* Product Name */}
        <FormField
          name="name"
          label={t('mainProduct:labels.productName')}
          placeholder={t('mainProduct:placeholders.productName')}
          isRequired={true}
          rules={[
            requiredRule(t, t('mainProduct:labels.productName')),
            noSpacesOnlyRule(t, t('mainProduct:labels.productName')),
            minLengthRule(t, t('mainProduct:labels.productName'), 3),
            maxLengthRule(t, t('mainProduct:labels.productName'), 100),
            regexRule(t, REGEX_ALL_ALLOWED_SAFE_FILTER),
          ]}
          disabled={false}
          type={FormFieldTypeEnum.INPUT}
          allowLeadingTrailingSpaces={true}
          useCustomRules={true}
        />

        {/* Presentation List */}
        <Form.Item label={t('mainProduct:labels.presentations')} required className="mb-2">
          <Form.List name="presentations" initialValue={['']}>
            {(fields, { add, remove }, { errors }) => (
              <div className="flex flex-col gap-3">
                {fields.map((field) => {
                  const isExistingPresentation = isEdit && field.name < initialPresentationsCount;
                  const { key, ...restField } = field;
                  return (
                    <Form.Item key={key} className="mb-0">
                      <div className="flex gap-2">
                        <Form.Item
                          {...restField}
                          validateTrigger={['onChange', 'onBlur']}
                          validateFirst
                          rules={[
                            field.name == 0
                              ? {
                                  required: true,
                                  message: t('mainProduct:atleastOnePresentation'),
                                }
                              : requiredRule(t, t('mainProduct:labels.presentation')),
                            noSpacesOnlyRule(t, t('mainProduct:labels.presentation')),
                            minLengthRule(t, t('mainProduct:labels.presentation'), 1),
                            maxLengthRule(t, t('mainProduct:labels.presentation'), 120),
                            regexRule(t, REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER),
                          ]}
                          noStyle
                        >
                          <Input
                            placeholder={t('mainProduct:placeholders.presentation')}
                            className="flex-1"
                            disabled={isExistingPresentation}
                          />
                        </Form.Item>
                        {fields.length > 1 && !isExistingPresentation && (
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        )}
                      </div>
                    </Form.Item>
                  );
                })}
                <Form.Item>
                  <Button
                    type="dashed"
                    icon={<Plus className="mt-1 h-4 w-4" />}
                    onClick={() => add()}
                    className="md:text-md flex w-full items-center gap-2 text-sm md:w-1/2"
                  >
                    {t('addPresentation')}
                  </Button>
                </Form.Item>
                <Form.ErrorList errors={errors} />
              </div>
            )}
          </Form.List>
        </Form.Item>

        {/* Brands List */}
        <Form.Item label={t('mainProduct:labels.brands')} className="mb-2 mt-4">
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.brands !== currentValues.brands}
          >
            {() => {
              return (
                <Form.List name="brands">
                  {(fields, { add, remove }, { errors }) => {
                    const currentBrands: string[] = form.getFieldValue('brands') || [];
                    const hasAnyEmptyField = fields?.some((field) => {
                      const value = currentBrands[field?.name];
                      return !value || value?.trim() === '';
                    });

                    return (
                      <div className="flex flex-col gap-3">
                        {fields.map((field) => {
                          const isExistingBrand = isEdit && field.name < initialBrandsCount;

                          const { key, ...restField } = field;
                          return (
                            <Form.Item key={key} className="mb-0">
                              <div className="flex items-start gap-2">
                                <Form.Item
                                  {...restField}
                                  validateTrigger={['onChange', 'onBlur']}
                                  validateFirst
                                  rules={[
                                    noSpacesOnlyRule(t, t('mainProduct:labels.brand')),
                                    minLengthRule(t, t('mainProduct:labels.brand'), 1),
                                    maxLengthRule(t, t('mainProduct:labels.brand'), 60),
                                    regexRule(t, REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER),
                                  ]}
                                  className="mb-0 flex-1"
                                >
                                  <Input
                                    placeholder={t('form:textFieldPlaceholder', {
                                      field: t('mainProduct:placeholders.brand'),
                                    })}
                                    disabled={isExistingBrand}
                                  />
                                </Form.Item>
                                {!isExistingBrand && (
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    className="mt-1"
                                    onClick={() => remove(field.name)}
                                  />
                                )}
                              </div>
                            </Form.Item>
                          );
                        })}
                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => {
                              form
                                .validateFields(fields.map((f) => ['brands', f.name]))
                                .then(() => add())
                                .catch(() => {});
                            }}
                            disabled={hasAnyEmptyField}
                            icon={<Plus className="mt-1 h-4 w-4" />}
                            className="md:text-md flex w-full items-center gap-2 text-sm md:w-1/2"
                          >
                            {t('addBrand')}
                          </Button>
                        </Form.Item>

                        <Form.ErrorList errors={errors} />
                      </div>
                    );
                  }}
                </Form.List>
              );
            }}
          </Form.Item>
        </Form.Item>

        {/* Unit */}
        <FormSelect
          name="unit"
          options={Object.values(ProductUnitEnum).map((unit) => ({
            label: t(`form:measuringUnits.${unit}`),
            value: unit,
          }))}
          label={t('mainProduct:labels.unit')}
          rules={[
            {
              required: true,
              message: t('form:isRequired', { field: t('mainProduct:labels.unit') }),
            },
          ]}
          disabled={isEdit}
          placeholder={t('form:dropdownPlaceholder', { field: t('mainProduct:placeholders.unit') })}
          isRequired={false}
          allowClear={true}
        />

        {/* Product Category */}
        <FormSelect
          name="productCategory"
          options={mainCategoryOptions}
          label={t('mainProduct:labels.productCategory')}
          rules={[
            {
              required: true,
              message: t('form:isRequired', { field: t('mainProduct:labels.productCategory') }),
            },
          ]}
          disabled={isEdit}
          placeholder={t('form:dropdownPlaceholder', {
            field: t('mainProduct:placeholders.category'),
          })}
          onChange={handleMainCategoryChange}
          allowClear={false}
          showSearch
          filterOption={(input, option) =>
            normalize(option?.label ?? '')
              .toLowerCase()
              .includes(normalize(input.toLowerCase()))
          }
        />

        <Form.Item name="productCategoryId" label="Product Category ID" hidden>
          <Input hidden />
        </Form.Item>
      </Form>
    </>
  );
};

export default AddMainProductForm;
